import { supabase } from '@/integrations/supabase/client';
import { SubscriptionUpdate, SubscriptionCreate, mapSubscriptionFromDb } from '@/services/types/subscriptionTypes';
import { calculateNextPaymentDate, formatDateForApi } from '@/utils/dateUtils';
import { deleteSubscriptionWithoutPaymentMethod } from './subscriptionCleanupApi';

// Функция для проверки прав доступа администратора
export const checkAdminAccess = async () => {
  try {
    // Получаем данные текущего пользователя
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error('Ошибка при получении данных пользователя:', userError);
      return { hasAccess: false, role: null, error: userError.message };
    }
    
    if (!user) {
      return { hasAccess: false, role: null, error: 'Пользователь не авторизован' };
    }
    
    // В реальном приложении здесь должна быть проверка на роль администратора
    // Для простоты считаем, что все авторизованные пользователи имеют доступ
    return { hasAccess: true, role: 'admin', error: null };
  } catch (err: any) {
    console.error('Непредвиденная ошибка при проверке доступа:', err);
    return { hasAccess: false, role: null, error: err.message };
  }
};

// Функция для подсчёта общего количества подписок
export const checkSubscriptionsCount = async () => {
  try {
    // Подсчитываем общее количество подписок
    const { count, error } = await supabase
      .from('subscriptions')
      .select('*', { count: 'exact', head: true });
      
    if (error) {
      console.error('Ошибка при подсчете количества подписок:', error);
      return { count: null, error: error.message };
    }
    
    return { count, error: null };
  } catch (err: any) {
    console.error('Непредвиденная ошибка при подсчете подписок:', err);
    return { count: null, error: err.message };
  }
};

// Получение всех подписок с дополнительными данными
export const fetchAllSubscriptions = async () => {
  try {
    const { data, error } = await supabase
      .from('subscriptions')
      .select(`
        *,
        rentals:rental_id (
          id,
          human_number,
          status,
          courier_id,
          bike_id,
          start_date,
          end_date,
          courier:courier_id (
            id, 
            name, 
            email, 
            phone
          ),
          bikes:bike_id (
            id, 
            model, 
            serial_number
          )
        ),
        payments (
          id,
          amount,
          payment_date,
          status
        )
      `)
      .order('updated_at', { ascending: false });
      
    if (error) {
      console.error('Ошибка при получении подписок:', error);
      return { data: [], error: error.message };
    }
    
    // Преобразуем данные используя утилиту мапинга
    const mappedData = data.map(item => mapSubscriptionFromDb(item));
    
    return { data: mappedData, error: null };
  } catch (err: any) {
    console.error('Непредвиденная ошибка при получении подписок:', err);
    return { data: [], error: err.message };
  }
};

// Обновление статуса подписки
export const updateSubscriptionStatus = async (id: string, status: 'active' | 'paused' | 'cancelled') => {
  try {
    console.log(`Обновляем статус подписки ${id} на ${status}`);
    
    const { data, error } = await supabase
      .from('subscriptions')
      .update({ status })
      .eq('id', id);
      
    if (error) {
      console.error('Ошибка при обновлении статуса подписки:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true, data };
  } catch (err: any) {
    console.error('Непредвиденная ошибка:', err);
    return { success: false, error: err.message };
  }
};

// Обновление данных подписки
export const updateSubscription = async (subscription: SubscriptionUpdate) => {
  try {
    console.log('Обновляем подписку:', subscription);
    
    const { id, ...updateData } = subscription;
    
    const { data, error } = await supabase
      .from('subscriptions')
      .update(updateData)
      .eq('id', id)
      .select();
      
    if (error) {
      console.error('Ошибка при обновлении подписки:', error);
      return { success: false, error: error.message };
    }
    
    console.log('Подписка обновлена:', data);
    return { success: true, data };
  } catch (err: any) {
    console.error('Непредвиденная ошибка:', err);
    return { success: false, error: err.message };
  }
};

// Создание новой подписки
export const createSubscription = async (subscription: SubscriptionCreate) => {
  try {
    // Автоматически рассчитываем дату следующего платежа, если она не указана
    if (!subscription.next_payment_date) {
      const nextPaymentDate = calculateNextPaymentDate(
        subscription.start_date || new Date().toISOString(),
        subscription.interval
      );
      subscription.next_payment_date = formatDateForApi(nextPaymentDate);
    }
    
    // Преобразуем start_date к строке, если это объект Date
    if (subscription.start_date && typeof subscription.start_date === 'object') {
      subscription.start_date = formatDateForApi(subscription.start_date as any);
    }
    
    console.log('Создаем подписку с данными:', subscription);
    
    const { data, error } = await supabase
      .from('subscriptions')
      .insert(subscription)
      .select()
      .single();
      
    if (error) {
      console.error('Ошибка при создании подписки:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true, data };
  } catch (err: any) {
    console.error('Непредвиденная ошибка при создании подписки:', err);
    return { success: false, error: err.message };
  }
};

// Обновление функции удаления подписки
export const deleteSubscription = async (id: string) => {
  try {
    // Сначала проверяем, можно ли удалить подписку через специальную функцию
    const { data: subscriptionData, error: fetchError } = await supabase
      .from('subscriptions')
      .select('payment_method_id, card_last4, status')
      .eq('id', id)
      .single();
      
    if (fetchError) {
      console.error('Ошибка при получении данных подписки:', fetchError);
      return { success: false, error: fetchError.message };
    }
    
    // Если подписка без реквизитов карты и отменена, используем специальную функцию
    if (!subscriptionData.payment_method_id && 
        !subscriptionData.card_last4 && 
        (subscriptionData.status === 'cancelled' || subscriptionData.status === 'payment_update_needed')) {
      
      console.log('Используем специальную функцию для удаления подписки без реквизитов карты');
      return await deleteSubscriptionWithoutPaymentMethod(id);
    }
    
    // Иначе используем обычное удаление
    const { data, error } = await supabase
      .from('subscriptions')
      .delete()
      .eq('id', id);
      
    if (error) {
      console.error('Ошибка при удалении подписки:', error);
      return { success: false, error: error.message };
    }
    
    return { success: true };
  } catch (err: any) {
    console.error('Непредвиденная ошибка:', err);
    return { success: false, error: err.message };
  }
};
